import React, { useEffect, useState } from "react";
import "./AppointmentReport.css";
import ENDPOINTS from "../../../../Constants/ApiEndpoints";
import axios from "axios";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaGift,
  FaMoneyBillWave,
  FaUserTag,
  FaSearch,
  FaUserMd,
  FaPhoneAlt,
  FaExternalLinkAlt,
  FaFilter,
  FaInfoCircle
} from "react-icons/fa";
import { Pagination } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DateRangePickerComponent from "../../../../CommonComponents/DateRangePickerComponent";

const AppointmentReport = () => {
  const navigate = useNavigate();

  // Date state defaulting to today
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const formatDateToIso = (dateObj) => {
    if (!dateObj) return "";
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const day = String(dateObj.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchAppointmentReport = async (start, end) => {
    setLoading(true);
    setError(null);
    try {
      const fromStr = formatDateToIso(start);
      const toStr = formatDateToIso(end);

      const response = await axios.get(`${ENDPOINTS.GET_APPOINTMENT_REPORT}`, {
        params: {
          fromDate: fromStr,
          toDate: toStr,
        },
      });

      setReportData(response.data);
    } catch (err) {
      console.error("Failed to fetch appointment report:", err);
      setError("Unable to load appointment report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointmentReport(startDate, endDate);
  }, [startDate, endDate]);

  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const handleRowClick = (appointmentId) => {
    if (appointmentId) {
      window.open(`/outpatient/appointments/patientInfoPanel/${appointmentId}`, "_blank");
    }
  };

  // Filter appointments list by search query
  const allAppointments = reportData?.appointmentList || [];
  const filteredAppointments = allAppointments.filter((item) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (item.patient && item.patient.toLowerCase().includes(q)) ||
      (item.mrNumber && item.mrNumber.toLowerCase().includes(q)) ||
      (item.doctor && item.doctor.toLowerCase().includes(q)) ||
      (item.phone && item.phone.toLowerCase().includes(q)) ||
      (item.type && item.type.toLowerCase().includes(q)) ||
      (item.status && item.status.toLowerCase().includes(q)) ||
      (item.bookedBy && item.bookedBy.toLowerCase().includes(q))
    );
  });

  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage) || 1;
  const paginatedAppointments = filteredAppointments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status) => {
    const s = (status || "SCHEDULED").toUpperCase();
    if (s === "COMPLETED" || s === "CONFIRMED") {
      return <span className="status-badge status-completed">COMPLETED</span>;
    }
    if (s === "CANCELLED") {
      return <span className="status-badge status-cancelled">CANCELLED</span>;
    }
    return <span className="status-badge status-scheduled">SCHEDULED</span>;
  };

  return (
    <div className="appointment-report-master-wrapper">
      <div className="unified-master-card">
        <div className="master-card-accent-bar" />

        {/* Master Toolbar Header: Date Range Picker + Search Bar */}
        <div className="master-toolbar-header">
          <div className="toolbar-left-group">
            <div className="toolbar-date-picker">
              <DateRangePickerComponent
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
              />
            </div>
            <div className="report-search-box">
              <FaSearch size={13} color="#94a3b8" className="search-icon" />
              <input
                type="text"
                placeholder="Search patient, MR No, doctor, status..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
          <button
            className="refresh-report-btn"
            onClick={() => fetchAppointmentReport(startDate, endDate)}
          >
            Refresh Report
          </button>
        </div>

        {/* KPI Summary Banner */}
        <div className="report-kpi-grid">
          <div className="kpi-card total-kpi">
            <div className="kpi-icon-wrapper blue-icon">
              <FaCalendarCheck size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">TOTAL APPOINTMENTS</span>
              <h3 className="kpi-value">{reportData?.totalAppointments || 0}</h3>
            </div>
          </div>

          <div className="kpi-card free-kpi">
            <div className="kpi-icon-wrapper green-icon">
              <FaGift size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">FREE APPOINTMENTS</span>
              <h3 className="kpi-value">{reportData?.freeAppointments || 0}</h3>
            </div>
          </div>

          <div className="kpi-card paid-kpi">
            <div className="kpi-icon-wrapper emerald-icon">
              <FaMoneyBillWave size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">PAID APPOINTMENTS</span>
              <h3 className="kpi-value">{reportData?.paidAppointments || 0}</h3>
            </div>
          </div>

          <div className="kpi-card referral-kpi">
            <div className="kpi-icon-wrapper purple-icon">
              <FaUserTag size={18} />
            </div>
            <div className="kpi-content">
              <span className="kpi-label">REFERRALS</span>
              <h3 className="kpi-value">{reportData?.referrals || 0}</h3>
            </div>
          </div>
        </div>

        {/* Loading / Error States */}
        {loading && (
          <div className="report-loading-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Fetching appointment report from backend...</p>
          </div>
        )}

        {error && !loading && (
          <div className="report-error-state">
            <FaInfoCircle size={20} color="#ef4444" />
            <p>{error}</p>
          </div>
        )}

        {/* Detailed Appointment Data Table */}
        {!loading && !error && (
          <div className="transactions-section">
            <div className="transactions-section-header">
              <div className="header-title-group">
                <FaCalendarAlt size={16} color="#2563eb" />
                <h3 className="table-title">Detailed Appointment Records</h3>
              </div>
              <span className="count-pill-badge">
                {filteredAppointments.length} Records
              </span>
            </div>

            <div className="table-responsive">
              <table className="report-data-table">
                <thead>
                  <tr>
                    <th style={{ width: "130px" }}>
                      <div className="th-flex">
                        <FaCalendarAlt size={11} color="#64748b" />
                        <span>DATE</span>
                      </div>
                    </th>
                    <th>
                      <div className="th-flex">
                        <FaUserTag size={11} color="#64748b" />
                        <span>PATIENT</span>
                      </div>
                    </th>
                    <th>
                      <div className="th-flex">
                        <FaUserMd size={11} color="#64748b" />
                        <span>DOCTOR</span>
                      </div>
                    </th>
                    <th style={{ width: "130px" }}>
                      <div className="th-flex">
                        <FaPhoneAlt size={11} color="#64748b" />
                        <span>PHONE</span>
                      </div>
                    </th>
                    <th style={{ width: "110px" }}>
                      <div className="th-flex">
                        <FaFilter size={11} color="#64748b" />
                        <span>BOOKING</span>
                      </div>
                    </th>
                    <th style={{ width: "120px" }}>
                      <div className="th-flex">
                        <span>BOOKED BY</span>
                      </div>
                    </th>
                    <th style={{ width: "120px" }}>
                      <div className="th-flex">
                        <span>REFERRAL</span>
                      </div>
                    </th>
                    <th style={{ width: "130px" }}>
                      <div className="th-flex">
                        <span>STATUS</span>
                      </div>
                    </th>
                    <th style={{ width: "80px" }} className="text-center">
                      <span>ACTION</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAppointments.length > 0 ? (
                    paginatedAppointments.map((appt, idx) => (
                      <tr
                        key={idx}
                        className="clickable-row"
                        onClick={() => handleRowClick(appt.appointmentId)}
                        title="Click to view patient details in new tab"
                      >
                        <td>
                          <span className="date-badge">{appt.date}</span>
                        </td>
                        <td>
                          <div className="patient-cell-info">
                            <strong className="patient-name-text">
                              {appt.patient || "N/A"}
                            </strong>
                            <span className="mr-subtext">{appt.mrNumber}</span>
                          </div>
                        </td>
                        <td>
                          <span className="doctor-name-tag">
                            {appt.doctor || "N/A"}
                          </span>
                        </td>
                        <td>
                          <span className="phone-tag">{appt.phone || "N/A"}</span>
                        </td>
                        <td>
                          <span className="type-pill">{appt.type || "NEW"}</span>
                        </td>
                        <td>
                          <span className="staff-tag">{appt.bookedBy || "System"}</span>
                        </td>
                        <td>
                          <span className="referral-tag">{appt.referral || "Self"}</span>
                        </td>
                        <td>{getStatusBadge(appt.status)}</td>
                        <td className="text-center">
                          <button
                            className="action-icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(appt.appointmentId);
                            }}
                            title="Open Patient Panel"
                          >
                            <FaExternalLinkAlt size={12} color="#2563eb" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="no-data-cell">
                        No appointments found for the selected criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            <div className="table-pagination-footer">
              <span className="pagination-info-text">
                Showing{" "}
                <strong>
                  {filteredAppointments.length === 0
                    ? 0
                    : (currentPage - 1) * itemsPerPage + 1}
                </strong>{" "}
                to{" "}
                <strong>
                  {Math.min(currentPage * itemsPerPage, filteredAppointments.length)}
                </strong>{" "}
                of <strong>{filteredAppointments.length}</strong> appointments
              </span>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(e, value) => setCurrentPage(value)}
                color="primary"
                variant="outlined"
                shape="rounded"
                size="small"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentReport;
